import {DocumentActionComponent, useClient} from 'sanity'
import {useState, useCallback, useMemo} from 'react'
import {BiTransfer} from 'react-icons/bi'

export const ChangeCategoryAction: DocumentActionComponent = (props) => {
  const {id, type, published} = props
  const client = useClient({apiVersion: '2025-01-01'})
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [targetCategoryId, setTargetCategoryId] = useState('')
  const [categories, setCategories] = useState<Array<{_id: string; title: string}>>([])
  const [postsToMove, setPostsToMove] = useState<Array<{_id: string; title: string}>>([])
  const [isLoadingPosts, setIsLoadingPosts] = useState(false)

  // Only show this action for published categories
  const shouldShowAction = useMemo(
    () => (type === 'blog.category' || type === 'blog.category-cluster') && Boolean(published),
    [type, published],
  )

  const handleOpen = useCallback(async () => {
    setIsDialogOpen(true)
    setIsLoading(true)
    setIsLoadingPosts(true)

    try {
      // Fetch all categories except the current one and all posts in the current category
      const [fetchedCategories, fetchedPosts] = await Promise.all([
        client.fetch<Array<{_id: string; title: string}>>(
          `*[_type == "blog.category" && _id != $currentId]{_id, title}|order(title asc)`,
          {currentId: id},
        ),
        client.fetch<Array<{_id: string; title: string}>>(
          `*[_type == "blog.post" && mainCategory._ref == $categoryId]{_id, title}|order(title asc)`,
          {categoryId: id},
        ),
      ])
      setCategories(fetchedCategories)
      setPostsToMove(fetchedPosts || [])
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsLoading(false)
      setIsLoadingPosts(false)
    }
  }, [client, id])

  const handleChangeCategory = useCallback(async () => {
    if (!targetCategoryId) {
      alert('Please select a target category')
      return
    }

    if (!postsToMove || postsToMove.length === 0) {
      alert('No posts found in this category')
      return
    }

    // Confirm the action
    const confirmed = confirm(
      `Are you sure you want to move ${postsToMove.length} post(s) from this category to the selected category?`,
    )

    if (!confirmed) {
      return
    }

    setIsLoading(true)

    try {
      // Update all posts in a transaction
      const transaction = client.transaction()

      postsToMove.forEach((post) => {
        transaction.patch(post._id, {
          set: {
            mainCategory: {
              _type: 'reference',
              _ref: targetCategoryId,
            },
          },
        })
      })

      await transaction.commit()

      alert(`Successfully moved ${postsToMove.length} post(s) to the new category`)
      setIsDialogOpen(false)
      setTargetCategoryId('')
      setPostsToMove([])
    } catch (error) {
      console.error('Error changing category:', error)
      alert('Failed to change category. Check the console for details.')
    } finally {
      setIsLoading(false)
    }
  }, [client, targetCategoryId, postsToMove])

  if (!shouldShowAction) {
    return null
  }

  return {
    label: 'Move Posts to Another Category',
    icon: BiTransfer,
    onHandle: handleOpen,
    dialog: isDialogOpen && {
      type: 'dialog',
      onClose: () => {
        setIsDialogOpen(false)
        setTargetCategoryId('')
      },
      header: 'Move Posts to Another Category',
      content: (
        <div style={{padding: '20px'}}>
          <div style={{display: 'flex', gap: '20px', marginBottom: '20px'}}>
            {/* Left column: Category selection */}
            <div style={{flex: 1}}>
              <p style={{marginBottom: '16px', fontWeight: 'bold'}}>
                Select target category:
              </p>

              {isLoading && <p>Loading categories...</p>}

              {!isLoading && categories.length === 0 && (
                <p>No other categories available. Create a category first.</p>
              )}

              {!isLoading && categories.length > 0 && (
                <div style={{marginBottom: '16px'}}>
                  <label htmlFor="target-category" style={{display: 'block', marginBottom: '8px'}}>
                    Target Category:
                  </label>
                  <select
                    id="target-category"
                    value={targetCategoryId}
                    onChange={(e) => setTargetCategoryId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  >
                    <option value="">-- Select a category --</option>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.title || 'Untitled'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Right column: Posts list */}
            <div
              style={{
                flex: 1,
                borderLeft: '1px solid #ccc',
                paddingLeft: '20px',
              }}
            >
              <p style={{marginBottom: '16px', fontWeight: 'bold'}}>
                Posts to move ({postsToMove.length}):
              </p>

              {isLoadingPosts && <p>Loading posts...</p>}

              {!isLoadingPosts && postsToMove.length === 0 && (
                <p style={{color: '#999'}}>No posts found in this category.</p>
              )}

              {!isLoadingPosts && postsToMove.length > 0 && (
                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <ul style={{margin: 0, padding: 0, listStyle: 'none'}}>
                    {postsToMove.map((post) => (
                      <li
                        key={post._id}
                        style={{
                          padding: '6px 8px',
                          fontSize: '13px',
                          borderBottom: '1px solid #f0f0f0',
                        }}
                      >
                        {post.title || 'Untitled Post'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div style={{display: 'flex', gap: '8px', justifyContent: 'flex-end'}}>
            <button
              type="button"
              onClick={() => {
                setIsDialogOpen(false)
                setTargetCategoryId('')
              }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                background: 'white',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleChangeCategory}
              disabled={isLoading || !targetCategoryId}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                borderRadius: '4px',
                border: 'none',
                background: targetCategoryId && !isLoading ? '#2276fc' : '#ccc',
                color: 'white',
                cursor: targetCategoryId && !isLoading ? 'pointer' : 'not-allowed',
              }}
            >
              {isLoading ? 'Moving...' : 'Move Posts'}
            </button>
          </div>
        </div>
      ),
    },
  }
}
